function getState(): Record<string, any> | null {
  const w = window as any;
  return (w.beatlineState || w.state || w.__beatlineTsLastState || null) as Record<string, any> | null;
}

function gameScreenIsActive(): boolean {
  const game = document.getElementById('game-screen') as HTMLElement | null;
  if (!game) return false;
  try { return getComputedStyle(game).display !== 'none' && getComputedStyle(game).visibility !== 'hidden'; }
  catch (_) { return !!game.offsetParent; }
}

function startOverlayIsVisible(): boolean {
  const overlay = document.getElementById('start-overlay') as HTMLElement | null;
  if (!overlay) return false;
  try { return getComputedStyle(overlay).display !== 'none' && getComputedStyle(overlay).visibility !== 'hidden'; }
  catch (_) { return false; }
}

function selectedPlayerCount(): number {
  const checked = document.querySelector<HTMLInputElement>('input[name="p_count"]:checked')?.value || '';
  const nDom = parseInt(checked, 10);
  const state = getState();
  const nState = parseInt(String(state?.playerCount ?? ''), 10);
  const activeGame = gameScreenIsActive() && !startOverlayIsVisible();
  if (activeGame && Number.isFinite(nState) && nState > 0) return nState;
  return Number.isFinite(nDom) && nDom > 0 ? nDom : (Number.isFinite(nState) && nState > 0 ? nState : 0);
}

function modeFromDomOrState(): string {
  const state = getState();
  const app = document.getElementById('beatline-app');
  const cls = app?.classList;
  if (state?.perfectOrder || cls?.contains('perfect-order')) return 'perfect';
  if (state?.timeAttack || cls?.contains('time-attack')) return 'timeattack';
  if (state?.survival || cls?.contains('survival')) return 'survival';
  if (state?.speedrun || cls?.contains('speedrun')) return 'speedrun';
  const selected = document.querySelector<HTMLInputElement>('input[name="solo_mode"]:checked')?.value || '';
  if (selectedPlayerCount() === 1 && selected !== 'speedrun') return selected || 'solo-classic';
  return selected;
}

function intValue(value: unknown, fallback: number): number {
  const n = parseInt(String(value ?? ''), 10);
  return Number.isFinite(n) ? n : fallback;
}

function selectedSpeedrunTarget(): number {
  const state = getState();
  const selected = document.querySelector<HTMLInputElement>('input[name="speedrun_cards"]:checked')?.value;
  return Math.max(1, intValue(state?.speedrunTarget ?? selected ?? 10, 10));
}

function currentDisplayedSpeedrunDone(target: number): number {
  const countEl = document.getElementById('active-score-count');
  const text = String(countEl?.textContent || '').trim();
  const m = text.match(/^(\d+)\s*\//);
  if (!m) return 0;
  return Math.max(0, Math.min(target, intValue(m[1], 0)));
}

function speedrunDone(): number {
  const state = getState();
  const target = selectedSpeedrunTarget();

  // C16: voorkom dat de safety-poll de score terugzet naar 0 wanneer de legacy
  // state niet globaal beschikbaar is. De echte score wordt door updateUI/special
  // delegates met context gezet; de poll mag die waarde alleen bewaren, niet wissen.
  if (!state) return currentDisplayedSpeedrunDone(target);

  const p1 = state?.p1 || {};
  const score = intValue(p1.score, NaN as any);
  const timelineCards = Array.isArray(p1.timeline) ? Math.max(0, p1.timeline.length - 1) : NaN;
  const candidates = [score, timelineCards, currentDisplayedSpeedrunDone(target)].filter((n) => Number.isFinite(n));
  const best = candidates.length ? Math.max(...candidates) : 0;
  return Math.max(0, Math.min(target, best));
}

function patchActiveScorePanel(): void {
  const panel = document.getElementById('active-score-panel');
  if (!panel) return;
  const mode = modeFromDomOrState();

  const playerCount = selectedPlayerCount();
  const hideForSolo = playerCount === 1 && mode !== 'speedrun';
  if (mode === 'survival' || mode === 'timeattack' || mode === 'perfect' || hideForSolo) {
    panel.style.setProperty('display', 'none', 'important');
    panel.setAttribute('hidden', 'hidden');
    panel.setAttribute('aria-hidden', 'true');
    return;
  }

  panel.removeAttribute('hidden');
  panel.setAttribute('aria-hidden', 'false');
  panel.style.setProperty('display', 'grid', 'important');
  panel.style.setProperty('visibility', 'visible', 'important');

  if (mode !== 'speedrun') return;

  const target = selectedSpeedrunTarget();
  const done = speedrunDone();
  const needed = Math.max(0, target - done);
  const lang = String((window as any).beatlineLang || 'nl').toLowerCase();
  const countEl = document.getElementById('active-score-count');
  const neededEl = document.getElementById('active-score-needed');
  if (countEl) countEl.textContent = `${done}/${target}`;
  panel.classList.toggle('is-complete', needed <= 0);
  if (neededEl) {
    neededEl.innerHTML = needed <= 0
      ? (lang === 'en'
        ? '<span class="active-score-panel__needed-word">DONE</span>'
        : '<span class="active-score-panel__needed-word">KLAAR</span>')
      : (lang === 'en'
        ? `<span class="active-score-panel__needed-word">NEED</span><strong class="active-score-panel__needed-number">${needed}</strong><span class="active-score-panel__needed-word">MORE</span>`
        : `<span class="active-score-panel__needed-word">NOG</span><strong class="active-score-panel__needed-number">${needed}</strong><span class="active-score-panel__needed-word">NODIG</span>`);
  }
}

function installAriaFocusGuard(): void {
  const hideFocusedGuard = (target: EventTarget | null) => {
    const el = target instanceof HTMLElement ? target : null;
    if (!el) return;
    if (el.id !== 'center-intervention' && el.id !== 'beatline-member-modal' && !el.classList.contains('center-intervention')) return;
    if (el.getAttribute('aria-hidden') !== 'true') return;
    const active = document.activeElement as HTMLElement | null;
    if (active && el.contains(active)) {
      active.blur();
      const fallback = document.getElementById('btn-check') || document.getElementById('btn-next') || document.getElementById('beatline-app');
      if (fallback instanceof HTMLElement) {
        fallback.focus?.({ preventScroll: true });
      }
    }
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
        hideFocusedGuard(mutation.target);
      }
      if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
        const el = mutation.target instanceof HTMLElement ? mutation.target : null;
        if (el && (el.id === 'center-intervention' || el.classList.contains('center-intervention')) && el.style.display === 'none') {
          const active = document.activeElement as HTMLElement | null;
          if (active && el.contains(active)) active.blur();
        }
      }
    }
  });

  const start = () => {
    document.querySelectorAll('#center-intervention, #beatline-member-modal').forEach((el) => {
      observer.observe(el, { attributes: true, attributeFilter: ['aria-hidden', 'style'] });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
}

export function installSpecialModePatches(): void {
  installAriaFocusGuard();

  const run = () => {
    try { patchActiveScorePanel(); } catch (_) {}
  };

  document.addEventListener('click', () => setTimeout(run, 0), true);
  window.addEventListener('beatline_data_loaded', run);
  window.addEventListener('beatline_mode_changed', run as EventListener);

  const app = document.getElementById('beatline-app');
  if (app) {
    new MutationObserver(run).observe(app, { attributes: true, attributeFilter: ['class'] });
  }

  document.addEventListener('change', () => setTimeout(run, 0), true);

  // Lichte safety-poll voor legacy special-mode updates, zonder feedback-loop en
  // zonder Speedrun terug naar 0 te zetten als er geen globale state beschikbaar is.
  window.setInterval(run, 750);
  run();
}
